import h from 'hyperscript';
import { Overlay } from 'ol';
import markerIcon from '@disy/cadenza-icons/map-marker.svg';

import { createClipboardButton } from 'ui/clipboard-button/clipboard-button';

import type { Map } from 'cadenza/map/map';
import { SketchDrawInteraction } from 'cadenza/workbook/sketch/interactions/sketch-draw-interaction';
import { icon } from 'cadenza/utils/icon/icon';
import { projectCoordinate } from 'cadenza/map/projection-utils';
import { formatCoordinate, formatCoordinateForCopy } from 'cadenza/map/format-coordinate';
import { SketchFeatureProperty } from 'cadenza/workbook/sketch/model/sketch-types';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { uniqueId } from 'cadenza/utils/unique-id';
import { getSrsForCoordinateDisplay } from 'cadenza/map/map-utils';

import type { StyleLike } from 'ol/style/Style';
import type { Point } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';

export class CaptureCoordinatesInteraction extends SketchDrawInteraction {

  override _map;
  _overlay!: Overlay; // added in

  constructor (map: Map, source: VectorSource, featureStyle: StyleLike) {
    super({ map, source, type: 'Point', featureStyle });
    this._map = map;
  }

  override onDrawStart (feature: Feature) {
    super.onDrawStart(feature);
    setupCoordinatesOverlay(feature, this._map);
  }

  _reset () {
    this._map.removeOverlay(this._overlay);
  }

  override onActivation () {
    super.onActivation();
    this._map.getTargetElement().classList.add('is-cursor-pointer');
  }

  override onDeactivation () {
    super.onDeactivation();
    this._reset();
    this._map.getTargetElement().classList.remove('is-cursor-pointer');
  }

}

function getCaptureCoordinateLayerOverlays (map: Map, source: VectorSource) {
  const overlays: Overlay[] = [];
  source.getFeatures().forEach(feature => {
    const overlayId = feature.get(SketchFeatureProperty.CAPTURE_COORDINATES_OVERLAY_ID);
    if (overlayId) {
      overlays.push(map.getOverlayById(overlayId));
    }
  });
  return overlays;
}

export function setupCoordinatesOverlay (feature: Feature, map: Map) {
  removeCaptureCoordinateFeatureOverlay(feature, map);
  const overlay = createCoordinatesOverlay(feature as Feature<Point>, map);
  map.addOverlay(overlay);
  feature.set(SketchFeatureProperty.CAPTURE_COORDINATES_OVERLAY_ID, overlay.getId());
}

export function removeCaptureCoordinateFeatureOverlay (feature: Feature, map: Map) {
  const currentFeatureOverlayId = feature.get(SketchFeatureProperty.CAPTURE_COORDINATES_OVERLAY_ID);
  if (currentFeatureOverlayId) {
    map.removeOverlay(map.getOverlayById(currentFeatureOverlayId));
  }
}

export function removeCaptureCoordinateOverlays (map: Map, source: VectorSource) {
  const overlays = getCaptureCoordinateLayerOverlays(map, source);
  overlays.forEach(overlay => map.removeOverlay(overlay));
}

export function setCaptureCoordinateOverlaysHidden (map: Map, source: VectorSource, isHidden: boolean) {
  getCaptureCoordinateLayerOverlays(map, source).forEach(overlay => {
    const overlayElement = overlay.getElement();
    if (overlayElement) {
      overlayElement.hidden = isHidden;
    }
  });
}

function createCoordinatesOverlay (feature: Feature<Point>, map: Map) {
  const backgroundMapProjection = map.getView().getProjection();

  const geometry = feature.getGeometry();
  assertNonNullable(geometry);
  const coordinates = geometry.getCoordinates();

  const srsForCoordinatesDisplay = getSrsForCoordinateDisplay(map);
  const coordinatesForDisplay = projectCoordinate(coordinates, backgroundMapProjection.getCode(), srsForCoordinatesDisplay);

  const copyCoordinatesButton = createClipboardButton(
    () => formatCoordinateForCopy(coordinatesForDisplay, srsForCoordinatesDisplay),
    { variant: 'primary' });
  return new Overlay({
    id: uniqueId(),
    element: h(
      '.tooltip.coordinates-tooltip',
      h('.d-stack-h.space-1', [
        icon(markerIcon, { size: 'xs', styleClass: 'coordinates-tooltip--marker-icon' }),
        formatCoordinate(coordinatesForDisplay, srsForCoordinatesDisplay),
        copyCoordinatesButton
      ])
    ) as HTMLElement,
    position: geometry.getLastCoordinate(),
    positioning: 'bottom-center',
    offset: [ 0, -12 ] // the height of the arrow below the tooltip is 12px
  });
}
